import { Fragment, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import api from "../lib/api";

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** Remounts a card when its :id changes so its form state reloads for the new record. */
export const RemountOnId = ({ children }) => {
  const { id } = useParams();
  return <Fragment key={id}>{children}</Fragment>;
};

/** Previous / Next record arrows, ordered by document number (or code/name). Alt+← / Alt+→ also work. */
export const RecordNav = ({ source, filter, currentId, sortKey, basePath, testId = "record-nav" }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { data = [] } = useQuery({ queryKey: [source], queryFn: async () => (await api.get(`/${source}`)).data, enabled: currentId !== "new" });
  const records = filter ? data.filter(filter) : data;
  const ordered = useMemo(() => [...records].sort((a, b) => collator.compare(sortKey(a) || "", sortKey(b) || "")), [records, sortKey]);
  const idx = ordered.findIndex((r) => r.id === currentId);
  const prev = idx > 0 ? ordered[idx - 1] : null;
  const next = idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : null;

  const go = (rec) => {
    if (!rec) { return; }
    document.activeElement?.blur?.();
    setTimeout(() => navigate(`${basePath}/${rec.id}${search}`), 60);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) { return; }
      e.preventDefault();
      go(e.key === "ArrowLeft" ? prev : next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (idx < 0 || ordered.length < 2) { return null; }
  const btn = "h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm transition-colors hover:bg-muted disabled:opacity-35 disabled:pointer-events-none";
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testId}>
      <button className={btn} onClick={() => go(prev)} disabled={!prev} title={prev ? `Previous: ${sortKey(prev)} (Alt+←)` : "First record"} data-testid={`${testId}-prev`}>
        <CaretLeft size={16} weight="bold" />
      </button>
      <span className="text-xs text-muted-foreground tabular min-w-[4.5rem] text-center" data-testid={`${testId}-position`}>
        {idx + 1} <span>of</span> {ordered.length}
      </span>
      <button className={btn} onClick={() => go(next)} disabled={!next} title={next ? `Next: ${sortKey(next)} (Alt+→)` : "Last record"} data-testid={`${testId}-next`}>
        <CaretRight size={16} weight="bold" />
      </button>
    </div>
  );
};
